import { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, Link, Copy, MessageSquare, FileText, Bot, Loader2, Tag, X, Plus, GitBranch, AlertCircle, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@easyflow/ui-frontend/components/ui/alert-dialog';
import { crmCampaignCommissioningFieldsSchema } from '@easyflow/shared';
import TargetingRuleBuilder from '@/components/crm/TargetingRuleBuilder';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { usePendingNavigation } from '@/hooks/usePendingNavigation';
import { useCampaignWorkflowDraft } from './useCampaignWorkflowDraft';
import { CampaignWorkflowDraftPanel } from './CampaignWorkflowDraftPanel';
import { CampaignDraftRulesSummary } from './CampaignWorkflowInfo';
import { CampaignCommissioningFields, type CampaignCommissioningValues, type CampaignCommissioningForm } from './CampaignCommissioningFields';

interface PipelineStage {
  id: string;
  name: string;
  color: string;
  position: number;
  is_won: boolean;
  is_lost: boolean;
  is_default: boolean;
  stage_type: 'entry' | 'wip' | 'appointment' | 'won' | 'lost' | 'archived';
}

interface Campaign extends CampaignCommissioningValues {
  id: string;
  name: string;
  description?: string;
  type: 'outbound_call' | 'sms' | 'whatsapp' | 'email' | 'general' | 'marketing' | 'booking' | 'service' | 'gtm' | 'lead';
  status: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled' | 'running';
  start_date?: string;
  end_date?: string;
  message_template?: string;
  target_filter?: { source: 'all' | 'leads' | 'contacts'; [key: string]: unknown };
  stats?: { total: number; contacted: number; responded: number; converted: number };
  allowed_stage_ids?: string[];
  automation_rule_ids?: string[];
  target_sectors?: string[];
  target_criteria?: Record<string, unknown> | null;
  targeting_rules?: { include: Array<{ field: string; operator: string; value: string }>; exclude: Array<{ field: string; operator: string; value: string }> } | null;
  contactability_template_id?: string | null;
  negative_text?: string;
  dialer_config?: { trunk_ids?: string[]; [k: string]: unknown } | null;
  is_active?: boolean;
  form_id?: string;
  utm_campaign_match?: string;
  whatsapp_number?: string;
  inbound_did?: string;
  chatbot_agent_ids?: string[];
  allowed_tags?: string[];
  metadata?: {
    inbound_destination_type?: string | null;
    inbound_destination_id?: string | null;
    [key: string]: unknown;
  };
  leads_total?: number;
  leads_won?: number;
  leads_lost?: number;
  leads_active?: number;
  revenue?: number;
  conversion_rate?: number;
  created_at: string;
}

interface EmbedForm {
  id: string;
  name: string;
  description?: string;
  slug: string;
  fields: Array<{ id: string; name: string; label: string; type: string; required: boolean; placeholder?: string; options?: string[] }>;
  default_stage_id?: string;
  stage_name?: string;
  stage_color?: string;
  redirect_url?: string;
  success_message?: string;
  styles?: Record<string, string>;
  is_active: boolean;
  submissions_count: number;
  created_at: string;
  campaign_id?: string;
  campaign_name?: string;
}

interface AutomationRule {
  id: string;
  name: string;
  description?: string;
  trigger_type: string;
  trigger_config: Record<string, unknown>;
  conditions: Array<{ id: string; field: string; operator: string; value: string }>;
  actions: Array<{ id: string; type: string; config: Record<string, unknown> }>;
  is_active: boolean;
  is_global?: boolean;
  trigger_count: number;
  last_triggered_at?: string;
  created_at: string;
}

interface PhoneNumber {
  id: string;
  phone_number: string;
  friendly_name?: string;
  type: 'sms' | 'whatsapp' | 'both';
  is_default: boolean;
}

interface ChatbotAgent {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
}

interface AvailableDid {
  id: string;
  did_number: string;
  route_name: string;
  description?: string;
  destination_type: string;
  destination_id?: string;
  enabled: boolean;
  assigned_campaign_id: string | null;
  assigned_campaign_name: string | null;
}

interface DestinationOption {
  id: string;
  extension_number: string;
  display_name: string;
  type: 'extension' | 'queue' | 'ring_group';
}

interface CampaignModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign | null;
  onSave: () => void;
  pipelineStages: PipelineStage[];
  automations: AutomationRule[];
  embedForms: EmbedForm[];
  phoneNumbers: PhoneNumber[];
  chatbotAgents: ChatbotAgent[];
  loading?: boolean;
}

type TargetingRulesState = { include: Array<{ field: string; operator: string; value: string }>; exclude: Array<{ field: string; operator: string; value: string }> } | null;

const defaultCommissioning: CampaignCommissioningForm = {
  business_driver_id: '',
  commissioning_customer_list_required: false,
};

const defaultForm = {
  name: '',
  description: '',
  type: 'general' as Campaign['type'],
  message_template: '',
  target_filter: { source: 'all' as 'all' | 'leads' | 'contacts' },
  start_date: '',
  end_date: '',
  allowed_stage_ids: [] as string[],
  automation_rule_ids: [] as string[],
  target_sectors: [] as string[],
  target_criteria: null as Record<string, unknown> | null,
  negative_text: '',
  contactability_template_id: '' as string,
  targeting_rules: null as TargetingRulesState,
  dialer_config: { trunk_ids: [] as string[] } as { trunk_ids: string[]; [k: string]: unknown },
  is_active: true,
  form_id: '',
  utm_campaign_match: '',
  whatsapp_number: '',
  inbound_did: '',
  chatbot_agent_ids: [] as string[],
  ...defaultCommissioning,
  _inbound_destination_type: '',
  _inbound_destination_id: '',
};

interface ContactabilityTemplate { id: string; name: string; description?: string; }
interface SipTrunk { id: string; name: string; provider?: string; is_active?: boolean; }

export default function CampaignModal(props: CampaignModalProps) {
  const [session, setSession] = useState(props);
  const [pendingSession, setPendingSession] = useState<CampaignModalProps | null>(null);
  const identity = `${props.open}:${props.campaign?.id ?? 'new'}`;
  const [observed, setObserved] = useState(identity);
  const dirty = useRef(false);
  if (identity !== observed) {
    setObserved(identity);
    if (dirty.current && session.open) setPendingSession(props);
    else setSession(props);
  }
  return <>
    <CampaignModalEditor {...props} key={`${session.open}:${session.campaign?.id ?? 'new'}`}
      open={session.open} campaign={session.campaign} onDirtyChange={value => { dirty.current = value; }} />
    <AlertDialog open={!!pendingSession} onOpenChange={open => { if (!open) setPendingSession(null); }}>
      <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Cambiare campagna?</AlertDialogTitle>
        <AlertDialogDescription>La campagna aperta contiene modifiche da salvare. Il cambio di contesto non le applicherà automaticamente.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel onClick={() => setPendingSession(null)}>Mantieni la bozza aperta</AlertDialogCancel>
          <AlertDialogAction onClick={() => { if (pendingSession) { dirty.current = false; setSession(pendingSession); setPendingSession(null); } }}>Scarta e cambia</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>;
}

function CampaignModalEditor({
  open,
  onOpenChange,
  campaign,
  onSave,
  pipelineStages,
  automations,
  embedForms,
  phoneNumbers,
  chatbotAgents,
  onDirtyChange,
}: CampaignModalProps & { onDirtyChange: (dirty: boolean) => void }) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('generale');
  const [saving, setSaving] = useState(false);
  const [form, setFormState] = useState({ ...defaultForm });
  const [formReady, setFormReady] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const setForm: React.Dispatch<React.SetStateAction<typeof form>> = value => {
    setFormDirty(true);
    setFormState(value);
  };
  const [availableDids, setAvailableDids] = useState<AvailableDid[]>([]);
  const [destinations, setDestinations] = useState<DestinationOption[]>([]);
  const [loadingDids, setLoadingDids] = useState(false);
  const [workflowValidationError, setWorkflowValidationError] = useState<{ message: string; unsupportedNodes: string[] } | null>(null);
  const workflowDraft = useCampaignWorkflowDraft(open, campaign?.id);
  // This controlled child also emits on mount. A hydration/no-op is not an edit;
  // a stable callback avoids its effect repeatedly notifying on every parent render.
  const handleTargetingChange = useCallback((rules: TargetingRulesState) => {
    setFormState(previous => {
      if (JSON.stringify(previous.targeting_rules) === JSON.stringify(rules)) return previous;
      setFormDirty(true);
      return { ...previous, targeting_rules: rules };
    });
  }, []);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const contextRef = useRef(campaign?.id);
  contextRef.current = campaign?.id;
  const dirty = !closing && (formDirty || workflowDraft.dirty);
  onDirtyChange(dirty);
  const navigationGuard = usePendingNavigation(open && dirty);
  const requestClose = (next: boolean) => {
    if (saving) return;
    if (!next && dirty) setDiscardOpen(true);
    else onOpenChange(next);
  };
  useEffect(() => {
    if (!open || !dirty) return;
    const guard = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [open, dirty]);
  const [contactabilityTemplates, setContactabilityTemplates] = useState<ContactabilityTemplate[]>([]);
  const [availableTrunks, setAvailableTrunks] = useState<SipTrunk[]>([]);

  useEffect(() => {
    let current = true;
    if (open) {
      setFormReady(true);
      setFormDirty(false);
      if (campaign) {
        const dialerCfg = campaign.dialer_config ?? {};
        setFormState({
          name: campaign.name || '',
          description: campaign.description || '',
          type: campaign.type || 'general',
          message_template: campaign.message_template || '',
          target_filter: campaign.target_filter || { source: 'all' },
          start_date: campaign.start_date || '',
          end_date: campaign.end_date || '',
          allowed_stage_ids: campaign.allowed_stage_ids || [],
          automation_rule_ids: campaign.automation_rule_ids || [],
          target_sectors: campaign.target_sectors || [],
          target_criteria: campaign.target_criteria || defaultForm.target_criteria,
          negative_text: campaign.negative_text || '',
          contactability_template_id: campaign.contactability_template_id || '',
          targeting_rules: (campaign.targeting_rules as TargetingRulesState) || null,
          dialer_config: {
            ...dialerCfg,
            trunk_ids: Array.isArray(dialerCfg.trunk_ids) ? dialerCfg.trunk_ids : [],
          },
          is_active: campaign.is_active ?? true,
          form_id: campaign.form_id || '',
          utm_campaign_match: campaign.utm_campaign_match || '',
          whatsapp_number: campaign.whatsapp_number || '',
          inbound_did: campaign.inbound_did || '',
          chatbot_agent_ids: [],
          business_driver_id: campaign.business_driver_id || '',
          commissioning_customer_list_required: campaign.commissioning_customer_list_required ?? false,
          _inbound_destination_type: (campaign.metadata?.inbound_destination_type) || '',
          _inbound_destination_id: (campaign.metadata?.inbound_destination_id) || '',
        });
        apiClient.get<string[]>(`/crm/campaigns/${campaign.id}/chatbot-agents`)
          .then(res => {
            if (current && contextRef.current === campaign.id && res.success && Array.isArray(res.data)) {
              const ids = res.data;
              setFormState(prev => ({ ...prev, chatbot_agent_ids: ids }));
            }
          })
          .catch(() => {});
      } else {
        setFormState({ ...defaultForm, dialer_config: { trunk_ids: [] } });
      }
      setActiveTab('generale');

      setLoadingDids(true);
      apiClient.get<{ dids: AvailableDid[]; destinations: DestinationOption[] }>('/crm/campaigns/available-dids')
        .then(res => {
            if (current && res.success && res.data) {
            setAvailableDids(res.data.dids || []);
            setDestinations(res.data.destinations || []);
          }
        })
        .catch(() => {})
        .finally(() => { if (current) setLoadingDids(false); });

      apiClient.get<ContactabilityTemplate[]>('/crm/contactability-templates')
        .then(res => {
            if (current && res.success && Array.isArray(res.data)) setContactabilityTemplates(res.data);
        })
        .catch(() => {});

      apiClient.get<SipTrunk[] | { trunks: SipTrunk[] }>('/sip-trunks')
        .then(res => {
          const payload = res as unknown;
          let trunks: unknown = [];
          if (Array.isArray(payload)) {
            trunks = payload;
          } else if (payload && typeof payload === 'object') {
            const obj = payload as { data?: unknown; trunks?: unknown };
            const dataField = obj.data;
            if (Array.isArray(dataField)) {
              trunks = dataField;
            } else if (dataField && typeof dataField === 'object') {
              trunks = (dataField as { trunks?: unknown }).trunks ?? [];
            } else if (Array.isArray(obj.trunks)) {
              trunks = obj.trunks;
            }
          }
          if (current) setAvailableTrunks(Array.isArray(trunks) ? (trunks as SipTrunk[]) : []);
        })
        .catch(() => {});
    }
    return () => { current = false; };
  }, [open, campaign]);

  const handleSave = async () => {
    // Validate only commissioning fields; retain all other campaign JSON unchanged.
    const commissioning = crmCampaignCommissioningFieldsSchema.safeParse({
      business_driver_id: form.business_driver_id || null,
      commissioning_customer_list_required: form.commissioning_customer_list_required,
    });
    if (!commissioning.success) {
      toast({ title: 'Configurazione commissioning non valida', description: 'Seleziona un driver con ID UUID valido oppure Nessun driver.', variant: 'destructive' });
      setActiveTab('generale');
      return;
    }
    if (!form.name.trim()) {
      toast({ title: 'Errore', description: 'Nome campagna obbligatorio', variant: 'destructive' });
      setActiveTab('generale');
      return;
    }

    const selectedStages = pipelineStages.filter(s => (form.allowed_stage_ids || []).includes(s.id));
    const requiredTypes = ['entry', 'appointment', 'won', 'lost'];
    const typeLabels: Record<string, string> = { entry: 'Ingresso', appointment: 'Appuntamento', won: 'Vinto', lost: 'Perso' };

    for (const type of requiredTypes) {
      const count = selectedStages.filter(s => s.stage_type === type).length;
      if (count === 0) {
        toast({ title: 'Stage mancante', description: `Stage ${typeLabels[type]} obbligatorio`, variant: 'destructive' });
        setActiveTab('pipeline');
        return;
      }
      if (count > 1) {
        toast({ title: 'Troppi stage', description: `Solo uno stage ${typeLabels[type]} permesso`, variant: 'destructive' });
        setActiveTab('pipeline');
        return;
      }
    }

    setSaving(true);
    try {
      const { _inbound_destination_type, _inbound_destination_id, contactability_template_id, dialer_config, targeting_rules, ...formData } = form;
      const payload: Record<string, unknown> = {
        ...formData,
        form_id: form.form_id || null,
        ...commissioning.data,
        contactability_template_id: contactability_template_id || null,
        targeting_rules: targeting_rules && (targeting_rules.include?.length || targeting_rules.exclude?.length) ? targeting_rules : null,
        dialer_config: form.type === 'outbound_call'
          ? { ...dialer_config, trunk_ids: Array.isArray(dialer_config?.trunk_ids) ? dialer_config.trunk_ids : [] }
          : (dialer_config && Object.keys(dialer_config).length > 1 ? dialer_config : null),
        metadata: {
          ...campaign?.metadata,
          inbound_destination_type: _inbound_destination_type || null,
          inbound_destination_id: _inbound_destination_id || null,
        },
      };
      Object.assign(payload, workflowDraft.payload());
      if (campaign) {
        await apiClient.put(`/crm/campaigns/${campaign.id}`, payload);
        toast({ title: 'Successo', description: 'Campagna aggiornata' });
      } else {
        await apiClient.post('/crm/campaigns', payload);
        toast({ title: 'Successo', description: 'Campagna creata' });
      }
      setClosing(true);
      onDirtyChange(false);
      onOpenChange(false);
      onSave();
    } catch (error: unknown) {
      // 422 dall'associazione canonica, envelope canonico (Task #4765): messaggio
      // in `error.message`, elementi non supportati in `error.details.unsupported_nodes`.
      type CanonicalError = { message?: string; details?: { unsupported_nodes?: string[] } };
      const err = error as Error & { status?: number; data?: { error?: CanonicalError | string } };
      if (err.status === 422 && err.data) {
        const canonical = typeof err.data.error === 'object' ? err.data.error : undefined;
        setWorkflowValidationError({
          message: canonical?.message || err.message,
          unsupportedNodes: canonical?.details?.unsupported_nodes ?? [],
        });
        setActiveTab('workflow');
      } else {
        console.error('Error saving campaign:', error);
        toast({ title: 'Errore', description: err.message || 'Errore nel salvataggio', variant: 'destructive' });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={requestClose}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        onEscapeKeyDown={event => { event.preventDefault(); requestClose(false); }}>
        <DialogHeader>
          <DialogTitle>{campaign ? 'Modifica Campagna' : 'Nuova Campagna'}</DialogTitle>
          <DialogDescription>Configura la campagna per gestire i tuoi lead</DialogDescription>
        </DialogHeader>

        <fieldset disabled={saving} className="min-w-0 contents">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex w-full flex-wrap h-auto gap-1 sm:grid sm:grid-cols-7">
            <TabsTrigger value="generale">Generale</TabsTrigger>
            <TabsTrigger value="targeting">Targeting</TabsTrigger>
            <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
            <TabsTrigger value="automazioni">Automazioni</TabsTrigger>
            <TabsTrigger value="workflow">Workflow</TabsTrigger>
            <TabsTrigger value="chatbot">Chatbot AI</TabsTrigger>
            <TabsTrigger value="tracking">Tracking</TabsTrigger>
          </TabsList>

          <TabsContent value="generale" className="space-y-4 mt-4 data-[state=inactive]:hidden" forceMount>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="campaign-name">Nome Campagna *</Label>
                <Input
                  id="campaign-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="es. Black Friday 2024"
                />
              </div>
              <div>
                <Label htmlFor="campaign-type">Tipo Campagna</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v as Campaign['type'] })}>
                  <SelectTrigger id="campaign-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">Generale</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="booking">Booking</SelectItem>
                    <SelectItem value="service">Servizio</SelectItem>
                    <SelectItem value="gtm">GTM</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="outbound_call">Chiamata Outbound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Descrizione</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Descrizione della campagna..."
                rows={2}
              />
            </div>

            <div>
              <Label>Testo Negativo</Label>
              <p className="text-xs text-muted-foreground mb-1">Testo da non utilizzare durante la conversazione (anti-script per AI Agent / script call center)</p>
              <Textarea
                value={form.negative_text}
                onChange={(e) => setForm({ ...form, negative_text: e.target.value })}
                placeholder="es. Non menzionare prezzi, non fare promesse di consegna…"
                rows={3}
              />
            </div>

            <div>
              <Label>Template Contattabilità</Label>
              <p className="text-xs text-muted-foreground mb-1">Regole orarie / canali consentiti per i contatti di questa campagna</p>
              <Select
                value={form.contactability_template_id || '__none__'}
                onValueChange={(v) => setForm({ ...form, contactability_template_id: v === '__none__' ? '' : v })}
              >
                <SelectTrigger><SelectValue placeholder="Nessun template (libero)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nessun template</SelectItem>
                  {contactabilityTemplates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}{t.description ? ` — ${t.description}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>


            <div className="grid grid-cols-2 gap-4 border-t pt-4">
              <div>
                <Label>Data Inizio</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Data Fine</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                />
              </div>
            </div>

            <CampaignCommissioningFields open={open} campaignId={campaign?.id} value={form}
              onChange={commissioning => setForm(previous => ({ ...previous, ...commissioning }))} />

            <div className="flex items-center justify-between border-t pt-4">
              <div>
                <Label>Campagna Attiva</Label>
                <p className="text-xs text-muted-foreground">Attiva o disattiva la campagna</p>
              </div>
              <Switch
                aria-label="Campagna attiva"
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
            </div>
          </TabsContent>

          <TabsContent value="targeting" className="space-y-4 mt-4 data-[state=inactive]:hidden" forceMount>
            {formReady && <TargetingRuleBuilder
              key={campaign?.id || 'new'}
              campaignId={campaign?.id}
              initialRules={form.targeting_rules ?? undefined}
              initialTemplateId={form.contactability_template_id || undefined}
              hideTemplateSelect
              onChange={handleTargetingChange}
            />}
            <p className="text-xs text-muted-foreground italic">
              Le regole verranno salvate insieme alla campagna quando premi <strong>Salva Campagna</strong>.
            </p>
          </TabsContent>

          <TabsContent value="pipeline" className="space-y-4 mt-4 data-[state=inactive]:hidden" forceMount>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Label className="text-base font-medium">Stati Pipeline da Usare</Label>
                <div className="relative group">
                  <div className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 text-xs flex items-center justify-center cursor-help">?</div>
                  <div className="absolute left-0 bottom-full mb-1 hidden group-hover:block z-50 w-64">
                    <div className="bg-card border border-border text-foreground text-xs rounded p-2 shadow-lg">
                      <p className="font-semibold mb-1">Tipologie Stage Obbligatorie</p>
                      <ul className="space-y-1 list-disc pl-3">
                        <li><strong>Ingresso (I):</strong> Stage iniziale dei nuovi lead</li>
                        <li><strong>Appuntamento (A):</strong> Lead con appuntamento fissato</li>
                        <li><strong>Vinto (V):</strong> Lead convertiti in clienti</li>
                        <li><strong>Perso (P):</strong> Lead non convertiti</li>
                      </ul>
                      <p className="mt-2 text-muted-foreground">Ogni campagna richiede almeno uno stage per ogni tipologia per tracciare correttamente le conversioni.</p>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Seleziona gli stati per questa campagna. Obbligatorio: almeno uno per Ingresso, Appuntamento, Vinto e Perso
              </p>
              {(() => {
                const selectedStages = pipelineStages.filter(s => (form.allowed_stage_ids || []).includes(s.id));
                const hasEntry = selectedStages.some(s => s.stage_type === 'entry');
                const hasAppointment = selectedStages.some(s => s.stage_type === 'appointment');
                const hasWon = selectedStages.some(s => s.stage_type === 'won');
                const hasLost = selectedStages.some(s => s.stage_type === 'lost');
                return (
                  <div className="flex flex-wrap gap-2 mb-3 text-xs">
                    <span className={`px-2 py-1 rounded ${hasEntry ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
                      Ingresso: {hasEntry ? '✓' : 'mancante'}
                    </span>
                    <span className={`px-2 py-1 rounded ${hasAppointment ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
                      Appuntamento: {hasAppointment ? '✓' : 'mancante'}
                    </span>
                    <span className={`px-2 py-1 rounded ${hasWon ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
                      Vinto: {hasWon ? '✓' : 'mancante'}
                    </span>
                    <span className={`px-2 py-1 rounded ${hasLost ? 'bg-muted text-muted-foreground' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
                      Perso: {hasLost ? '✓' : 'mancante'}
                    </span>
                  </div>
                );
              })()}
              <div className="flex flex-wrap gap-2">
                {pipelineStages.map((stage) => {
                  const typeLabels: Record<string, string> = { entry: 'I', wip: 'W', appointment: 'A', won: 'V', lost: 'P' };
                  return (
                    <div
                      key={stage.id}
                      onClick={() => {
                        const current = form.allowed_stage_ids || [];
                        const updated = current.includes(stage.id)
                          ? current.filter(s => s !== stage.id)
                          : [...current, stage.id];
                        setForm({ ...form, allowed_stage_ids: updated });
                      }}
                      className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer transition-colors ${
                        (form.allowed_stage_ids || []).includes(stage.id)
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                          : 'hover:bg-muted/50'
                      }`}
                    >
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }} />
                      <span className="text-sm">{stage.name}</span>
                      <span className="text-[10px] px-1 py-0.5 bg-muted rounded font-medium">
                        {typeLabels[stage.stage_type] || 'W'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="automazioni" className="space-y-4 mt-4 data-[state=inactive]:hidden" forceMount>
            <div>
              <Label className="text-base font-medium">Regole di Automazione</Label>
              <p className="text-xs text-muted-foreground mb-2">Associazioni da salvare con Salva Campagna. Le regole ereditate non si eliminano da qui; le sostituzioni si scelgono nel tab Workflow.</p>
              <CampaignDraftRulesSummary draft={workflowDraft} />
              <div className="space-y-2">
                {automations.filter(a => !a.is_global && !workflowDraft.state?.rules.some(rule => rule.rule_id === a.id && rule.inherited)).map((automation) => (
                  <label
                    key={automation.id}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      (form.automation_rule_ids || []).includes(automation.id)
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30'
                        : 'hover:bg-muted/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={(form.automation_rule_ids || []).includes(automation.id)}
                      disabled={saving}
                      onChange={event => setForm(previous => ({ ...previous, automation_rule_ids: event.target.checked
                        ? [...previous.automation_rule_ids, automation.id]
                        : previous.automation_rule_ids.filter(id => id !== automation.id) }))}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-medium text-sm">{automation.name} · {automation.is_active ? 'Definizione abilitata' : 'Definizione disabilitata'}</p>
                      <p className="text-xs text-muted-foreground">{automation.description}</p>
                    </div>
                  </label>
                ))}
                {automations.filter(a => a.is_active).length === 0 && (
                  <p className="text-sm text-muted-foreground italic">Nessuna automazione attiva disponibile</p>
                )}
              </div>
            </div>

            <div className="border-t pt-4">
              <Label className="text-base font-medium">Form Web Collegato</Label>
              <p className="text-xs text-muted-foreground mb-2">Collega un form web per acquisire lead automaticamente in questa campagna</p>
              <Select
                value={form.form_id || 'none'}
                onValueChange={(v) => setForm({ ...form, form_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona un form..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun form collegato</SelectItem>
                  {embedForms.filter(f => f.is_active).map((ef) => (
                    <SelectItem key={ef.id} value={ef.id}>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span>{ef.name}</span>
                        <span className="text-xs text-muted-foreground">({ef.submissions_count} submission)</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {embedForms.filter(f => f.is_active).length === 0 && (
                <p className="text-xs text-muted-foreground mt-2 italic">Nessun form attivo. Crea un form nella sezione Impostazioni.</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="workflow" className="space-y-4 mt-4 data-[state=inactive]:hidden" forceMount>
            {open && <CampaignWorkflowDraftPanel draft={workflowDraft} campaignId={campaign?.id} />}
          </TabsContent>

          <TabsContent value="chatbot" className="space-y-4 mt-4 data-[state=inactive]:hidden" forceMount>
            <div>
              <Label className="text-base font-medium flex items-center gap-2">
                <Bot className="w-4 h-4" />
                Chatbot AI Collegato
              </Label>
              <p className="text-xs text-muted-foreground mb-3">
                Collega un chatbot AI per generare lead automaticamente in questa campagna
              </p>
              <div className="space-y-2">
                {chatbotAgents.length > 0 ? (
                  chatbotAgents.map((agent) => (
                    <div
                      key={agent.id}
                      onClick={() => {
                        const current = form.chatbot_agent_ids || [];
                        const updated = current.includes(agent.id)
                          ? current.filter(a => a !== agent.id)
                          : [...current, agent.id];
                        setForm({ ...form, chatbot_agent_ids: updated });
                      }}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        (form.chatbot_agent_ids || []).includes(agent.id)
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                          : 'hover:bg-muted/50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={(form.chatbot_agent_ids || []).includes(agent.id)}
                        onChange={() => {}}
                        className="w-4 h-4"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{agent.name}</p>
                        {agent.description && (
                          <p className="text-xs text-muted-foreground">{agent.description}</p>
                        )}
                      </div>
                      <Badge variant={agent.is_active ? 'default' : 'secondary'} className="text-xs">
                        {agent.is_active ? 'Attivo' : 'Inattivo'}
                      </Badge>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    Nessun chatbot disponibile. Crea un chatbot nella sezione AI Tools.
                  </p>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="tracking" className="space-y-4 mt-4 data-[state=inactive]:hidden" forceMount>
            <div className="flex flex-wrap gap-2 mb-2">
              <Badge variant={form.utm_campaign_match && form.whatsapp_number ? 'default' : 'secondary'} className="text-xs">
                <MessageSquare className="w-3 h-3 mr-1" />
                WhatsApp {form.utm_campaign_match && form.whatsapp_number ? '✓ attivo' : '— non configurato'}
              </Badge>
              <Badge variant={form.inbound_did ? 'default' : 'secondary'} className="text-xs">
                <Phone className="w-3 h-3 mr-1" />
                Voce {form.inbound_did ? '✓ attivo' : '— non configurato'}
              </Badge>
              {form.type === 'outbound_call' && (
                <Badge variant={(form.dialer_config?.trunk_ids?.length ?? 0) > 0 ? 'default' : 'secondary'} className="text-xs">
                  <Phone className="w-3 h-3 mr-1" />
                  Trunk Outbound {(form.dialer_config?.trunk_ids?.length ?? 0) > 0 ? `✓ ${form.dialer_config.trunk_ids.length}` : '— non configurato'}
                </Badge>
              )}
            </div>

            {form.type === 'outbound_call' && (
              <div className="border rounded-lg p-3 bg-blue-50 dark:bg-blue-950/30/40">
                <Label className="text-base font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                  Trunk SIP Outbound (Round-Robin)
                </Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Seleziona uno o più trunk SIP per le chiamate uscenti. Le chiamate verranno distribuite in round-robin tra i trunk selezionati.
                </p>
                {availableTrunks.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">Nessun trunk SIP disponibile. Configura i trunk nella sezione VoIP.</p>
                ) : (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {availableTrunks.map((trunk) => {
                      const checked = (form.dialer_config?.trunk_ids || []).includes(trunk.id);
                      return (
                        <label
                          key={trunk.id}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded border cursor-pointer text-sm transition-colors ${checked ? 'border-blue-500 bg-card' : 'border-transparent hover:bg-card'}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const cur = form.dialer_config?.trunk_ids || [];
                              const next = cur.includes(trunk.id) ? cur.filter(id => id !== trunk.id) : [...cur, trunk.id];
                              setForm({ ...form, dialer_config: { ...form.dialer_config, trunk_ids: next } });
                            }}
                            className="w-4 h-4"
                          />
                          <span className="font-medium">{trunk.name}</span>
                          {trunk.provider && <span className="text-xs text-muted-foreground">({trunk.provider})</span>}
                          {trunk.is_active === false && <Badge className="text-[10px] bg-muted text-muted-foreground">inattivo</Badge>}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="text-base font-medium flex items-center gap-2">
                Riconciliazione Inbound WhatsApp
                <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  Tracking campagna
                </span>
              </Label>
              <p className="text-xs text-muted-foreground mb-3">
                Quando un cliente clicca sul link WhatsApp dalla landing page o risponde con il codice campagna,
                verrà automaticamente associato a questa campagna. Il sistema cerca il codice nel messaggio inbound
                e assegna il lead alla campagna corrispondente.
              </p>
            </div>

            <div>
              <Label className="text-sm font-medium">Numero WhatsApp/SMS</Label>
              <p className="text-xs text-muted-foreground mb-2">Seleziona il numero da usare per i link di questa campagna</p>
              <Select
                value={form.whatsapp_number || 'none'}
                onValueChange={(v) => setForm({ ...form, whatsapp_number: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona un numero..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun numero selezionato</SelectItem>
                  {phoneNumbers.map((phone) => (
                    <SelectItem key={phone.id} value={phone.phone_number}>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span>{phone.friendly_name || phone.phone_number}</span>
                        <span className="text-xs text-muted-foreground">({phone.phone_number})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {phoneNumbers.length === 0 && (
                <p className="text-xs text-muted-foreground mt-2 italic">Nessun numero configurato. Configura i numeri nella sezione Messaggistica.</p>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium">Codice UTM Campaign</Label>
              <p className="text-xs text-muted-foreground mb-2">I messaggi inbound che contengono questo codice verranno associati a questa campagna</p>
              <Input
                value={form.utm_campaign_match}
                onChange={(e) => setForm({ ...form, utm_campaign_match: e.target.value.toUpperCase().replace(/\s+/g, '_') })}
                placeholder="es. PROMO_ESTATE_2024, BLACK_FRIDAY"
                className="uppercase"
              />
            </div>

            <div className="border-t border-border pt-4">
              <Label className="text-base font-medium flex items-center gap-2">
                Riconciliazione Inbound Chiamate
                <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  DID match
                </span>
              </Label>
              <p className="text-xs text-muted-foreground mb-3">
                Quando arriva una chiamata al numero DID configurato, il sistema crea un lead a 3 livelli:
                <strong> Layer 1</strong> (DID match), <strong>Layer 2</strong> (agente umano), <strong>Layer 3</strong> (agente AI con trascrizione).
              </p>
              <Label className="text-sm font-medium">Numero DID inbound</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Seleziona il numero DID da associare a questa campagna.
              </p>
              {loadingDids ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Caricamento DID...
                </div>
              ) : availableDids.length > 0 ? (
                <Select
                  value={form.inbound_did || 'none'}
                  onValueChange={(v) => setForm({ ...form, inbound_did: v === 'none' ? '' : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona un DID..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun DID selezionato</SelectItem>
                    {availableDids.map((did) => {
                      const isAssigned = did.assigned_campaign_id && did.assigned_campaign_id !== campaign?.id;
                      return (
                        <SelectItem key={did.id} value={did.did_number} disabled={!!isAssigned}>
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            <span>{did.did_number}</span>
                            <span className="text-xs text-muted-foreground">({did.route_name})</span>
                            {isAssigned && (
                              <span className="text-xs text-orange-500">- usato da {did.assigned_campaign_name}</span>
                            )}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              ) : (
                <div>
                  <Input
                    value={form.inbound_did || ''}
                    onChange={(e) => setForm({ ...form, inbound_did: e.target.value })}
                    placeholder="es. +390299999999"
                  />
                  <p className="text-xs text-muted-foreground mt-1 italic">Nessun DID configurato nelle rotte inbound. Inserisci manualmente o configura le rotte.</p>
                </div>
              )}

              {form.inbound_did && destinations.length > 0 && (
                <div className="mt-3">
                  <Label className="text-sm font-medium">Destinazione chiamata (opzionale)</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Seleziona estensione, coda o gruppo di squillo per instradare le chiamate di questa campagna.
                  </p>
                  <Select
                    value={form._inbound_destination_id ? `${form._inbound_destination_type}:${form._inbound_destination_id}` : 'none'}
                    onValueChange={(v) => {
                      if (v === 'none') {
                        setForm({ ...form, _inbound_destination_type: '', _inbound_destination_id: '' });
                      } else {
                        const [type, id] = v.split(':');
                        setForm({ ...form, _inbound_destination_type: type || '', _inbound_destination_id: id || '' });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Destinazione predefinita della rotta" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Destinazione predefinita della rotta</SelectItem>
                      {destinations.map((dest) => (
                        <SelectItem key={`${dest.type}:${dest.id}`} value={`${dest.type}:${dest.id}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-xs px-1.5 py-0.5 bg-muted rounded font-medium">
                              {dest.type === 'extension' ? 'Int' : dest.type === 'queue' ? 'Coda' : 'Gruppo'}
                            </span>
                            <span>{dest.extension_number} - {dest.display_name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {form.inbound_did && (
                <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-3 mt-3 space-y-1">
                  <p className="text-xs text-green-700 dark:text-green-300 font-medium">Tracking Voce Attivo</p>
                  <p className="text-xs text-green-600 dark:text-green-300">
                    <strong>Layer 1:</strong> Ogni chiamata al DID <strong>{form.inbound_did}</strong> crea/aggiorna un lead con fonte "inbound_call".
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-300">
                    <strong>Layer 2:</strong> Se risponde un agente, il lead viene arricchito con assegnatario e durata.
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-300">
                    <strong>Layer 3:</strong> Se risponde un AI Agent, il lead viene arricchito con dati dalla trascrizione (nome, interesse, sentiment).
                  </p>
                </div>
              )}
            </div>

            {form.utm_campaign_match && form.whatsapp_number && (
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                  <Link className="w-4 h-4" />
                  <span className="font-medium text-sm">Link WhatsApp per Landing Page</span>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-card border border-green-300 rounded px-3 py-2 text-sm font-mono text-muted-foreground break-all">
                    https://wa.me/{form.whatsapp_number.replace(/\D/g, '')}?text={encodeURIComponent(form.utm_campaign_match)}
                  </code>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const link = `https://wa.me/${form.whatsapp_number.replace(/\D/g, '')}?text=${encodeURIComponent(form.utm_campaign_match)}`;
                      navigator.clipboard.writeText(link);
                      toast({ title: 'Copiato!', description: 'Link copiato negli appunti' });
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-green-600 dark:text-green-300">
                  Usa questo link nel bottone "Contattaci su WhatsApp" della tua landing page
                </p>

                <div className="border-t border-green-200 dark:border-green-800 pt-3 mt-3">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-300 mb-2">
                    <MessageSquare className="w-4 h-4" />
                    <span className="font-medium text-sm">Suggerimento per Template</span>
                  </div>
                  <p className="text-xs text-green-600 dark:text-green-300 mb-2">
                    Nel template messaggio, chiedi al cliente di rispondere con il codice campagna:
                  </p>
                  <code className="block bg-card border border-green-300 rounded px-3 py-2 text-sm text-muted-foreground">
                    "Rispondi <strong>{form.utm_campaign_match}</strong> per ricevere maggiori informazioni"
                  </code>
                </div>
              </div>
            )}

            {form.utm_campaign_match && !form.whatsapp_number && (
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-xs text-blue-600 dark:text-blue-300">
                  <strong>Nota:</strong> Seleziona un numero WhatsApp/SMS sopra per generare il link diretto per la landing page.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
        </fieldset>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" disabled={saving} onClick={() => requestClose(false)}>Annulla</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvataggio...
              </>
            ) : (
              'Salva Campagna'
            )}
          </Button>
        </div>
      </DialogContent>

      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Scartare le modifiche alla campagna?</AlertDialogTitle>
            <AlertDialogDescription>
              Aggiunte, rimozioni e sostituzioni non sono ancora salvate. Nessuna operazione verrà applicata alla campagna.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continua a modificare</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setDiscardOpen(false); setClosing(true); onDirtyChange(false); onOpenChange(false); }}>Scarta modifiche</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {navigationGuard}
      {workflowValidationError && (
        <Dialog open={!!workflowValidationError} onOpenChange={(open) => { if (!open) setWorkflowValidationError(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-300">
                <AlertCircle className="w-5 h-5" />
                Workflow Incompatibile
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">{workflowValidationError.message}</p>
            {workflowValidationError.unsupportedNodes.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Elementi non supportati in campagna:</p>
                <div className="flex flex-wrap gap-1.5">
                  {workflowValidationError.unsupportedNodes.map(node => (
                    <Badge key={node} variant="outline" className="bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 border-blue-200 dark:border-blue-800">{node}</Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Modifica il workflow nel Workflow Builder oppure rimuovilo dalla campagna e riprova.</p>
              </div>
            )}
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setWorkflowValidationError(null)}>Chiudi</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}
